import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { useEffect, useState } from 'react'
import { COPIED_FEEDBACK_MS, CopyButton } from '../buttons/copy-button.tsx'
import * as styles from './copyable-text-field.css.ts'
import { InputContainer } from './input-container.tsx'
import * as textInputStyles from './text-input.css.ts'

export type CopyableTextFieldProps = {
  id: string
  label: string
  /** The text shown and copied. Always supplied by the caller — never composed from the page's own origin. */
  value: string
  helperText?: string
  /**
   * A second helper line below the first, for a fact about the value that is not an error — the
   * field is not marked invalid and nothing is disabled because of it.
   */
  secondaryHelperText?: string
  /** Accessible name of the copy control, e.g. "Copy webhook URL". */
  copyLabel: string
}

/**
 * A read-only value the user has to move into another product, with a copy control. Read-only rather
 * than disabled so the text stays selectable and readable by assistive technology.
 */
export const CopyableTextField: React.FC<CopyableTextFieldProps> = ({
  id,
  label,
  value,
  helperText,
  secondaryHelperText,
  copyLabel,
}) => {
  const [isCopyAnnounced, setIsCopyAnnounced] = useState(false)

  useEffect(() => {
    if (!isCopyAnnounced) {
      return
    }
    // `CopyButton`'s own constant, so the announcement and the check mark clear together rather than
    // needing a second value kept in step with it by hand.
    const timeout = setTimeout(() => setIsCopyAnnounced(false), COPIED_FEEDBACK_MS)
    return () => clearTimeout(timeout)
  }, [isCopyAnnounced])

  return (
    <div className={styles.field}>
      <InputContainer id={id} label={label} helperText={helperText}>
        <div className={textInputStyles.inputContainer({ size: 'medium' })}>
          <input id={id} className={textInputStyles.textInput({ size: 'medium' })} value={value} readOnly />
          <span className={styles.copyButtonSlot}>
            <CopyButton copyText={value} label={copyLabel} size="medium" onCopy={() => setIsCopyAnnounced(true)} />
          </span>
        </div>
      </InputContainer>
      {secondaryHelperText && <p className={styles.secondaryHelperText}>{secondaryHelperText}</p>}
      <VisuallyHidden>
        <span role="status" aria-live="polite">
          {isCopyAnnounced ? 'Copied' : ''}
        </span>
      </VisuallyHidden>
    </div>
  )
}
